import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { EventContext } from '@/redux/modules/analytics';
import { getCatalogIsInstantAutopay } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getIsRegistered,
    getRegistrationSuspended,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { CompleteAccountModalContext, ModalContext, openRegisterForAuctionModal } from '@/redux/modules/modal';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import { useTrackRegisterForAuctionPressedAnalytics } from '@/utils/amplitude/analytics/registration';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { CatalogRegistrationMessagesExperiment } from './CatalogRegistrationMessagesExperiment';

type Props = {
    catalogId: number;
    className?: string;
};

export const TimedPlusAuctionStatusExperiment = ({ catalogId, className }: Props) => {
    const formatMessage = useAppFormatMessage();
    const dispatch = useAppDispatch();
    const isWhiteLabel = useAppSelector(getIsWhiteLabel);
    const registered = useAppSelector((state) => getIsRegistered(state, catalogId));
    const suspended = useAppSelector((state) => getRegistrationSuspended(state, catalogId));
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));

    const trackRegisterForAuctionPressedAnalytics = useTrackRegisterForAuctionPressedAnalytics();

    const modalContext = useMemo<ModalContext>(
        () => ({
            catalogId,
            eventContext: EventContext.registerForAuction,
            id: catalogId,
            type: CompleteAccountModalContext.RegisterForAuction,
        }),
        [catalogId]
    );

    const registerForCatalog = () => {
        trackRegisterForAuctionPressedAnalytics();
        dispatch(
            openRegisterForAuctionModal({
                catalogId,
                fromBiddingConsole: false,
                isInstantAutopay,
                modalContext,
            })
        );
    };

    return (
        <div
            className={cn('flex h-full max-w-full flex-col items-center justify-start gap-xs', className)}
            data-testid="timed-plus-auction-status-experiment"
        >
            {suspended ? (
                <Button
                    className="w-full"
                    data-testid="catalogAccountSuspendedButton"
                    disabled
                    variant="filled"
                >
                    {formatMessage(['account_suspended'])}
                </Button>
            ) : (
                !registered && (
                    <Button
                        className="w-full"
                        data-testid="liveCatalogRegisterButton"
                        onClick={registerForCatalog}
                        style={isWhiteLabel ? 'primary' : 'critical'}
                        variant="filled"
                    >
                        {formatMessage(['catalogPage', 'catalogStatus', 'registerToBid'])}
                    </Button>
                )
            )}
            {registered && (
                <CatalogRegistrationMessagesExperiment
                    catalogId={catalogId}
                    className="w-full"
                />
            )}
        </div>
    );
};
